/**
 * Stale-while-revalidate voor GET-endpoints die geen gesynchroniseerde entiteit zijn
 * (instellingen). Dit bestand bevat de framework-vrije kern; de React-hook staat in
 * use-cached-get.ts.
 *
 * DRIE LAGEN, ÉÉN WAARHEID
 *   1. geheugen (Map per tab)     → instant bij remount/tabwissel, weg bij herladen;
 *   2. IndexedDB (response-cache) → instant na herladen, per gebruiker + groep gesleuteld;
 *   3. de server                  → altijd geraadpleegd ná het tonen; verschil wordt doorgezet.
 *
 * INVALIDATIE
 *   - Elke geslaagde niet-GET via api-client roept notifyApiMutation(url) aan. Entries waarvan het
 *     pad "verwant" is aan het gemuteerde pad (zie pathsRelated) worden uit beide caches gegooid en
 *     alle hooks op die paden halen opnieuw op. Een BroadcastChannel doet hetzelfde in andere
 *     browsertabbladen van dezelfde gebruiker.
 *   - Hooks revalideren daarnaast bij focus/zichtbaar worden (gedebounced), zodat wijzigingen van
 *     een collega of een cron binnen een paar seconden na terugkeer zichtbaar zijn.
 *   - Groepswissel/uitloggen: client-sync wist de IndexedDB-cache al; het geheugen gaat via
 *     clearCachedGetMemory() mee (sync-provider roept dat op dezelfde plek aan).
 *
 * NIET CACHEN: endpoints met geheimen, eenmalige statussen of testknoppen (Tuya-test, API-keys,
 * Stripe-onboarding). Caching is opt-in per hook-aanroep, nooit automatisch voor elke apiGet.
 */

export type MemoryEntry = { data: unknown; at: number };

const memory = new Map<string, MemoryEntry>();

export function readMemory<T>(url: string): { data: T; at: number } | null {
  const e = memory.get(url);
  return e ? { data: e.data as T, at: e.at } : null;
}

export function writeMemory(url: string, data: unknown): void {
  memory.set(url, { data, at: Date.now() });
}

export function clearCachedGetMemory(): void {
  memory.clear();
}

/** Pad zonder query/hash; trailing slash weg. */
export function urlPath(url: string): string {
  const q = url.search(/[?#]/);
  const p = q === -1 ? url : url.slice(0, q);
  return p.length > 1 && p.endsWith('/') ? p.slice(0, -1) : p;
}

/**
 * Verwant = gelijk, of de één is een segment-prefix van de ander.
 *   PATCH /api/settings/addons        ↔ GET /api/settings/addons?x=1   (gelijk pad)
 *   DELETE /api/group-users/abc       ↔ GET /api/group-users           (mutatie dieper)
 *   PATCH /api/groups/website         ↔ GET /api/groups/website/stats  (mutatie ondieper)
 * Niet verwant: /api/settings/addons ↔ /api/settings/addons-extra (geen segmentgrens).
 */
export function pathsRelated(a: string, b: string): boolean {
  const pa = urlPath(a);
  const pb = urlPath(b);
  if (pa === pb) return true;
  return pb.startsWith(pa + '/') || pa.startsWith(pb + '/');
}

type Listener = (mutatedPath: string) => void;
const listeners = new Set<Listener>();

export function subscribeMutations(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const CHANNEL_NAME = 'pm-cached-get';
let channel: BroadcastChannel | null = null;
function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!channel) {
    try {
      channel = new BroadcastChannel(CHANNEL_NAME);
      channel.onmessage = (ev: MessageEvent) => {
        const path = (ev.data as { path?: unknown } | null)?.path;
        if (typeof path === 'string') applyMutation(path, false);
      };
    } catch {
      channel = null;
    }
  }
  return channel;
}

function applyMutation(path: string, broadcast: boolean): void {
  for (const key of Array.from(memory.keys())) {
    if (pathsRelated(key, path)) memory.delete(key);
  }
  for (const l of Array.from(listeners)) {
    try {
      l(path);
    } catch {
      /* één kapotte listener mag de rest niet tegenhouden */
    }
  }
  if (broadcast) {
    try {
      getChannel()?.postMessage({ path });
    } catch {
      /* geen cross-tab, lokaal is al bijgewerkt */
    }
  }
}

/**
 * Aangeroepen door api-client na een geslaagde POST/PUT/PATCH/DELETE (en door de outbox bij
 * afspelen). Alleen relatieve /api-paden tellen; externe URL's worden genegeerd.
 */
export function notifyApiMutation(url: string): void {
  if (!url.startsWith('/api/')) return;
  const path = urlPath(url);
  applyMutation(path, true);
  if (typeof window !== 'undefined') {
    // IndexedDB-entries met hetzelfde pad weg; lazy import houdt idb uit bundels die het niet nodig hebben.
    void import('./response-cache')
      .then(({ invalidateCachedResponses }) => invalidateCachedResponses(`|${path}`))
      .catch(() => undefined);
  }
}

/** Zorgt dat dit tabblad mutaties van andere tabbladen ontvangt (idempotent). */
export function ensureMutationChannel(): void {
  getChannel();
}
